import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import Canvas from "../layouts/dahboard_canvas";
import { buildApiUrl, getStaffDetails, useBaseUrl } from "../../utils/utils";

interface StaffDirectoryItem {
    id: number;
    full_name: string;
    email: string;
    clinic_name?: string | null;
    title_name?: string | null;
    is_current_user: boolean;
}

interface ConversationParticipant {
    staff: number;
    staff_name: string;
    role: "admin" | "member";
    clinic_name?: string | null;
}

interface ConversationMessage {
    message_id: string;
    sender_staff_id?: number | null;
    sender_name?: string;
    message_type: "text" | "system" | "ai";
    content: string;
    created_at: string;
}

interface ConversationItem {
    conversation_id: string;
    subject: string;
    context_type: "general" | "collaboration" | "system";
    requires_password_reentry: boolean;
    is_group: boolean;
    unread_count: number;
    is_locked: boolean;
    collaboration?: number | null;
    last_message?: ConversationMessage | null;
    participants: ConversationParticipant[];
}

interface TypingState {
    staff: number;
    staff_name: string;
}

interface PaginationState {
    current_page: number;
    total_pages: number;
    total_items: number;
    has_next: boolean;
    has_previous: boolean;
}

const emptyPagination: PaginationState = {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false,
};

const MessagingWorkspacePage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const collaborationId = searchParams.get("collaboration_id");
    const requestedConversationId = searchParams.get("conversation");
    const requestedContextType = searchParams.get("context_type");
    const accessContext = collaborationId ? "collaboration" : "general";
    const pollTimerRef = useRef<number | null>(null);
    const typingTimerRef = useRef<number | null>(null);
    const typingExpiryRef = useRef<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    const [staffId, setStaffId] = useState<number | null>(null);
    const [institutionId, setInstitutionId] = useState<number | null>(null);
    const [clinicName, setClinicName] = useState("Facility");
    const [directory, setDirectory] = useState<StaffDirectoryItem[]>([]);
    const [conversations, setConversations] = useState<ConversationItem[]>([]);
    const [selectedConversationId, setSelectedConversationId] = useState("");
    const [selectedConversation, setSelectedConversation] = useState<ConversationItem | null>(null);
    const [messages, setMessages] = useState<ConversationMessage[]>([]);
    const [typingStates, setTypingStates] = useState<TypingState[]>([]);
    const [conversationPagination, setConversationPagination] = useState<PaginationState>(emptyPagination);
    const [messagePagination, setMessagePagination] = useState<PaginationState>(emptyPagination);
    const [contextFilter, setContextFilter] = useState<"all" | "general" | "collaboration" | "system">(
        requestedContextType === "general" || requestedContextType === "collaboration" || requestedContextType === "system"
            ? requestedContextType
            : collaborationId
                ? "collaboration"
                : "all"
    );
    const [search, setSearch] = useState("");
    const [draftText, setDraftText] = useState("");
    const [unlockPassword, setUnlockPassword] = useState("");
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeSubject, setComposeSubject] = useState("");
    const [composeParticipants, setComposeParticipants] = useState<number[]>([]);
    const [composeOpeningMessage, setComposeOpeningMessage] = useState("");
    const [pageError, setPageError] = useState("");
    const [loadingWorkspace, setLoadingWorkspace] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [savingConversation, setSavingConversation] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [draftingReply, setDraftingReply] = useState(false);

    const otherStaff = useMemo(() => directory.filter((item) => !item.is_current_user), [directory]);

    const stats = useMemo(() => {
        const unread = conversations.reduce((sum, item) => sum + (item.unread_count || 0), 0);
        const collaborationThreads = conversations.filter((item) => item.context_type === "collaboration").length;
        const lockedThreads = conversations.filter((item) => item.is_locked).length;
        return {
            total: conversationPagination.total_items || conversations.length,
            unread,
            collaborationThreads,
            lockedThreads,
        };
    }, [conversationPagination.total_items, conversations]);

    const showToast = (icon: "success" | "error" | "info" | "warning", title: string, text?: string) => {
        Swal.fire({
            toast: true,
            position: "top-end",
            icon,
            title,
            text,
            showConfirmButton: false,
            timer: icon === "error" ? 4200 : 2400,
            timerProgressBar: true,
            customClass: { popup: "uzima-toast-popup" },
        });
    };

    const showModalError = (title: string, text: string) => {
        Swal.fire({
            icon: "error",
            title,
            text,
            customClass: { popup: "uzima-modal-popup" },
            confirmButtonText: "Continue",
        });
    };

    const relativeTime = (value?: string) => {
        if (!value) return "No activity yet";
        const diffMinutes = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
        if (diffMinutes < 60) return `${diffMinutes} min ago`;
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} hr ago`;
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    };

    const formatTime = (value?: string) => {
        if (!value) return "";
        try {
            return new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
        } catch {
            return value;
        }
    };

    const readHeaders = (json = false) => ({
        ...(json ? { "Content-Type": "application/json" } : {}),
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
    });

    const readIdentityParams = () => {
        const params = new URLSearchParams();
        if (staffId) params.set("staff_id", String(staffId));
        if (institutionId) params.set("institution_id", String(institutionId));
        return params;
    };

    const readJson = async (path: string, init?: RequestInit) => {
        const response = await fetch(buildApiUrl(baseUrl, path), { credentials: "include", ...init });
        const payload = await response.json().catch(() => ({}));
        return { response, payload };
    };

    const handleApiError = (payload: any, fallback: string) => {
        const message = payload?.message || payload?.error || fallback;
        throw new Error(message);
    };

    const loadDirectory = async (collabId?: string | null) => {
        const params = readIdentityParams();
        if (collabId) params.set("collaboration_id", collabId);
        const { response, payload } = await readJson(`communications/api/messaging/directory/?${params.toString()}`, { headers: readHeaders() });
        if (!response.ok) handleApiError(payload, "Unable to load the staff directory.");
        setDirectory(Array.isArray(payload?.data?.staff) ? payload.data.staff : []);
    };

    const loadConversations = async (page = 1, keepSelection = true) => {
        const params = readIdentityParams();
        params.set("page", String(page));
        params.set("page_size", "12");
        params.set("access_context", accessContext);
        if (search.trim()) params.set("search", search.trim());
        if (contextFilter !== "all") params.set("context_type", contextFilter);
        const { response, payload } = await readJson(`communications/api/messaging/conversations/?${params.toString()}`, { headers: readHeaders() });
        if (!response.ok) handleApiError(payload, "Unable to load conversations.");
        const items = Array.isArray(payload?.data?.items) ? payload.data.items : [];
        setConversations(items);
        setConversationPagination(payload?.data?.pagination || emptyPagination);
        const preferredConversationId = keepSelection
            ? selectedConversationId || requestedConversationId || items[0]?.conversation_id || ""
            : items[0]?.conversation_id || "";
        if (preferredConversationId) {
            setSelectedConversationId((current) => current || preferredConversationId);
            const matched = items.find((item: ConversationItem) => item.conversation_id === (selectedConversationId || requestedConversationId || preferredConversationId));
            if (matched) setSelectedConversation(matched);
        }
    };

    const markConversationRead = async (conversationId: string) => {
        const { response } = await readJson(`communications/api/messaging/conversations/${conversationId}/read/`, {
            method: "POST",
            headers: readHeaders(true),
            body: JSON.stringify({ staff_id: staffId, institution_id: institutionId, access_context: accessContext }),
        });
        if (!response.ok) return;
    };

    const loadMessages = async (conversationId: string, page = 1, silent = false) => {
        if (!silent) setLoadingMessages(true);
        const params = readIdentityParams();
        params.set("page", String(page));
        params.set("page_size", "40");
        params.set("access_context", accessContext);
        const { response, payload } = await readJson(`communications/api/messaging/conversations/${conversationId}/messages/?${params.toString()}`, { headers: readHeaders() });
        if (!silent) setLoadingMessages(false);
        if (response.status === 423) {
            setMessages([]);
            setTypingStates([]);
            setSelectedConversation(conversations.find((item) => item.conversation_id === conversationId) || null);
            handleApiError(payload, "Secure conversation requires password re-entry.");
        }
        if (!response.ok) handleApiError(payload, "Unable to load messages.");
        setSelectedConversation(payload?.data?.conversation || null);
        setMessages(Array.isArray(payload?.data?.items) ? payload.data.items : []);
        setTypingStates(Array.isArray(payload?.data?.typing) ? payload.data.typing : []);
        setMessagePagination(payload?.data?.pagination || emptyPagination);
        await markConversationRead(conversationId);
    };

    const loadCollaborationConversation = async () => {
        if (!collaborationId) return false;
        const params = readIdentityParams();
        const { response, payload } = await readJson(`communications/api/messaging/collaborations/${collaborationId}/conversation/?${params.toString()}`, { headers: readHeaders() });
        if (!response.ok) handleApiError(payload, "Unable to resolve the collaboration conversation.");
        if (Array.isArray(payload?.data?.directory)) setDirectory(payload.data.directory);
        if (payload?.data?.conversation?.conversation_id) {
            setSelectedConversationId(payload.data.conversation.conversation_id);
            setSelectedConversation(payload.data.conversation);
            return true;
        }
        return false;
    };

    const refreshWorkspace = async (page = 1) => {
        if (!staffId) return;
        setLoadingWorkspace(true);
        setPageError("");
        try {
            const hasCollaborationThread = await loadCollaborationConversation();
            if (!hasCollaborationThread) await loadDirectory(collaborationId);
            await loadConversations(page);
        } catch (error: any) {
            setPageError(error?.message || "Unable to load messaging right now.");
        } finally {
            setLoadingWorkspace(false);
        }
    };

    const createConversation = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!composeSubject.trim() || composeParticipants.length === 0) {
            showModalError("Missing details", "Add a subject and at least one participant to create a conversation.");
            return;
        }
        setSavingConversation(true);
        try {
            const { response, payload } = await readJson("communications/api/messaging/conversations/create/", {
                method: "POST",
                headers: readHeaders(true),
                body: JSON.stringify({
                    staff_id: staffId,
                    institution_id: institutionId,
                    subject: composeSubject.trim(),
                    participant_ids: composeParticipants,
                    initial_message: composeOpeningMessage.trim(),
                    context_type: collaborationId ? "collaboration" : "general",
                    collaboration_id: collaborationId ? Number(collaborationId) : null,
                    access_context: accessContext,
                }),
            });
            if (!response.ok) handleApiError(payload, "Unable to create the conversation.");
            const conversationId = payload?.data?.conversation?.conversation_id;
            setComposeOpen(false);
            setComposeSubject("");
            setComposeParticipants([]);
            setComposeOpeningMessage("");
            await refreshWorkspace(1);
            if (conversationId) setSelectedConversationId(conversationId);
            showToast("success", "Conversation created", payload?.message || "Your messaging thread is ready.");
        } catch (error: any) {
            showModalError("Unable to create conversation", error?.message || "Please try again.");
        } finally {
            setSavingConversation(false);
        }
    };

    const sendTypingState = async (isTyping: boolean) => {
        if (!selectedConversationId) return;
        await readJson(`communications/api/messaging/conversations/${selectedConversationId}/typing/`, {
            method: "POST",
            headers: readHeaders(true),
            body: JSON.stringify({ staff_id: staffId, institution_id: institutionId, is_typing: isTyping, access_context: accessContext }),
        });
    };

    const sendMessage = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedConversationId || !draftText.trim()) return;
        setSendingMessage(true);
        try {
            const { response, payload } = await readJson(`communications/api/messaging/conversations/${selectedConversationId}/send/`, {
                method: "POST",
                headers: readHeaders(true),
                body: JSON.stringify({ staff_id: staffId, institution_id: institutionId, content: draftText.trim(), access_context: accessContext }),
            });
            if (!response.ok) handleApiError(payload, "Unable to send your message.");
            setDraftText("");
            await loadMessages(selectedConversationId, 1);
            await loadConversations(conversationPagination.current_page || 1);
        } catch (error: any) {
            showToast("error", "Message not sent", error?.message || "Please try again.");
        } finally {
            setSendingMessage(false);
        }
    };

    const unlockConversation = async () => {
        if (!selectedConversationId || !unlockPassword.trim()) {
            showModalError("Password required", "Enter your password to unlock this collaboration conversation.");
            return;
        }
        try {
            const { response, payload } = await readJson(`communications/api/messaging/conversations/${selectedConversationId}/unlock/`, {
                method: "POST",
                headers: readHeaders(true),
                body: JSON.stringify({ staff_id: staffId, institution_id: institutionId, password: unlockPassword.trim() }),
            });
            if (!response.ok) handleApiError(payload, "Unable to unlock this conversation.");
            setUnlockPassword("");
            await loadMessages(selectedConversationId, 1);
            showToast("success", "Conversation unlocked", payload?.message || "Secure access restored.");
        } catch (error: any) {
            showModalError("Unlock failed", error?.message || "Please verify your password and try again.");
        }
    };

    const draftReplyWithAi = async () => {
        if (!selectedConversation) return;
        setDraftingReply(true);
        try {
            const transcript = messages.slice(-8).map((item) => `${item.sender_name || "User"}: ${item.content}`).join("\n");
            const { response, payload } = await readJson("ai/ollama/chat/", {
                method: "POST",
                headers: readHeaders(true),
                body: JSON.stringify({
                    messages: [
                        { role: "system", content: "Draft a concise, professional healthcare communication reply. Return only the reply body." },
                        { role: "user", content: `Conversation subject: ${selectedConversation.subject}\n${transcript}` },
                    ],
                }),
            });
            if (!response.ok) handleApiError(payload, "AI could not generate a reply draft.");
            setDraftText(payload?.message?.content || "");
            showToast("success", "Draft ready", "AI filled the composer with a suggested reply.");
        } catch (error: any) {
            showToast("error", "AI draft failed", error?.message || "Please try again.");
        } finally {
            setDraftingReply(false);
        }
    };

    const selectConversation = (conversation: ConversationItem) => {
        setPageError("");
        setSelectedConversationId(conversation.conversation_id);
        setSelectedConversation(conversation);
    };

    useEffect(() => {
        document.title = "Uzima - Messaging Hub";
        const initialize = async () => {
            const info: any = await getStaffDetails();
            if (!info?.staff_id) {
                setPageError("Authentication required. Please log in again.");
                return;
            }
            setStaffId(info.staff_id || null);
            setInstitutionId(info?.institution_id || null);
            setClinicName(info?.clinic_name || info?.institution || "Facility");
        };
        initialize();
    }, []);

    useEffect(() => {
        if (!staffId) return;
        refreshWorkspace(1);
    }, [staffId, contextFilter]);

    useEffect(() => {
        if (!staffId) return;
        const timer = window.setTimeout(() => refreshWorkspace(1), 280);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!selectedConversationId || !staffId) return;
        loadMessages(selectedConversationId, 1).catch((error: any) => {
            setPageError(error?.message || "Unable to load this conversation.");
        });
    }, [selectedConversationId, staffId]);

    useEffect(() => {
        if (!messagesEndRef.current) return;
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length, typingStates.length]);

    useEffect(() => {
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        if (!staffId) return;
        pollTimerRef.current = window.setInterval(() => {
            loadConversations(conversationPagination.current_page || 1).catch(() => null);
            if (selectedConversationId) loadMessages(selectedConversationId, messagePagination.current_page || 1, true).catch(() => null);
        }, 5000);
        return () => {
            if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        };
    }, [staffId, selectedConversationId, conversationPagination.current_page, messagePagination.current_page, search, contextFilter]);

    useEffect(() => {
        if (!selectedConversationId) return;
        if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
        if (typingExpiryRef.current) window.clearTimeout(typingExpiryRef.current);
        if (!draftText.trim()) {
            sendTypingState(false).catch(() => null);
            return;
        }
        typingTimerRef.current = window.setTimeout(() => sendTypingState(true).catch(() => null), 220);
        typingExpiryRef.current = window.setTimeout(() => sendTypingState(false).catch(() => null), 2600);
        return () => {
            if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
        };
    }, [draftText, selectedConversationId]);

    const conversationBody = () => {
        if (pageError && !selectedConversation) {
            return (
                <div className="sc-empty">
                    <i className="bx bx-error-circle"></i>
                    <h3>Messaging isn&apos;t ready yet</h3>
                    <p>{pageError}</p>
                    <button type="button" className="sc-btn sc-btn-primary" onClick={() => refreshWorkspace(1)}>Try again</button>
                </div>
            );
        }

        if (!selectedConversation) {
            return (
                <div className="sc-empty">
                    <i className="bx bx-message-rounded-detail"></i>
                    <h3>Select a conversation</h3>
                    <p>Choose a thread from the left or start a new one to begin secure messaging.</p>
                    <button type="button" className="sc-btn sc-btn-primary" onClick={() => setComposeOpen(true)}>Start a conversation</button>
                </div>
            );
        }

        if (selectedConversation.is_locked && accessContext !== "collaboration" && messages.length === 0) {
            return (
                <div className="msg-lock-state">
                    <div className="msg-lock-icon"><i className="bx bx-lock-alt"></i></div>
                    <h3>Secure collaboration thread</h3>
                    <p>Re-enter your password to open this collaboration conversation outside the collaboration workspace.</p>
                    <div className="msg-lock-form">
                        <input
                            type="password"
                            className="sc-input"
                            value={unlockPassword}
                            onChange={(event) => setUnlockPassword(event.target.value)}
                            placeholder="Enter your password"
                        />
                        <button type="button" className="sc-btn sc-btn-primary" onClick={unlockConversation}>Unlock conversation</button>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="msg-thread-body">
                    {loadingMessages ? (
                        <div className="sc-empty" style={{ padding: "42px 20px" }}>
                            <i className="bx bx-loader-circle bx-spin"></i>
                            <h3>Loading conversation</h3>
                            <p>Fetching the latest messages and typing state.</p>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="sc-empty" style={{ padding: "42px 20px" }}>
                            <i className="bx bx-chat"></i>
                            <h3>No messages yet</h3>
                            <p>Use the composer below to send the first message in this thread.</p>
                        </div>
                    ) : (
                        messages.map((item) => {
                            const mine = item.sender_staff_id === staffId;
                            return (
                                <div key={item.message_id} className={`msg-bubble-row ${mine ? "mine" : "theirs"}`}>
                                    <div className={`msg-bubble ${mine ? "mine" : item.message_type === "ai" ? "ai" : "theirs"}`}>
                                        <div className="msg-bubble-head">
                                            <span>{mine ? "You" : item.sender_name || "Team member"}</span>
                                            <small>{formatTime(item.created_at)}</small>
                                        </div>
                                        <div className="msg-bubble-text">{item.content}</div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    {typingStates.length > 0 ? (
                        <div className="msg-typing-row">
                            <div className="msg-typing-pill">
                                <span className="msg-typing-dots"><span></span><span></span><span></span></span>
                                {typingStates.map((item) => item.staff_name).join(", ")} typing...
                            </div>
                        </div>
                    ) : null}
                    <div ref={messagesEndRef}></div>
                </div>
                <div className="msg-composer-wrap">
                    <div className="msg-composer-toolbar">
                        <div className="msg-composer-hint">
                            <i className="bx bx-shield-quarter"></i>
                            {selectedConversation.context_type === "collaboration"
                                ? "Secure collaboration messaging with password re-entry outside collaboration context"
                                : "Instant staff messaging with live typing indicators"}
                        </div>
                        <div className="msg-composer-actions">
                            <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={draftingReply} onClick={draftReplyWithAi}>
                                <i className={`bx ${draftingReply ? "bx-loader-circle bx-spin" : "bx-bot"}`}></i>
                                AI Draft
                            </button>
                            <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!messagePagination.has_previous} onClick={() => loadMessages(selectedConversationId, messagePagination.current_page - 1).catch((error: any) => showToast("error", "Unable to open older messages", error?.message || "Please try again."))}>
                                <i className="bx bx-chevron-left"></i>
                                Older
                            </button>
                            <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!messagePagination.has_next} onClick={() => loadMessages(selectedConversationId, messagePagination.current_page + 1).catch((error: any) => showToast("error", "Unable to open newer messages", error?.message || "Please try again."))}>
                                Newer
                                <i className="bx bx-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                    <form className="msg-composer" onSubmit={sendMessage}>
                        <textarea
                            className="sc-textarea msg-composer-input"
                            value={draftText}
                            onChange={(event) => setDraftText(event.target.value)}
                            placeholder={selectedConversation.context_type === "collaboration" ? "Write a secure collaboration update..." : "Write a message..."}
                        />
                        <div className="msg-composer-footer">
                            <small>Page {messagePagination.current_page} of {messagePagination.total_pages}</small>
                            <button type="submit" className="sc-btn sc-btn-primary" disabled={sendingMessage || !draftText.trim()}>
                                <i className={`bx ${sendingMessage ? "bx-loader-circle bx-spin" : "bx-send"}`}></i>
                                {sendingMessage ? "Sending..." : "Send message"}
                            </button>
                        </div>
                    </form>
                </div>
            </>
        );
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div>
                                <h1>{clinicName} Messaging Hub</h1>
                                <p>Modern secure conversations, collaboration lock controls, typing presence, and AI-assisted replies.</p>
                            </div>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button type="button" className="sc-btn sc-btn-outline" onClick={() => refreshWorkspace(1)}>
                                    <i className="bx bx-refresh"></i>
                                    Refresh
                                </button>
                                <button type="button" className="sc-btn sc-btn-primary" onClick={() => setComposeOpen(true)}>
                                    <i className="bx bx-plus"></i>
                                    New conversation
                                </button>
                            </div>
                        </div>

                        <div className="sc-stats">
                            <div className="sc-stat">
                                <div className="sc-stat-icon teal"><i className="bx bx-message-rounded-detail"></i></div>
                                <div className="sc-stat-value">{stats.total}</div>
                                <div className="sc-stat-label">Visible threads</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon blue"><i className="bx bx-envelope"></i></div>
                                <div className="sc-stat-value">{stats.unread}</div>
                                <div className="sc-stat-label">Unread messages</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon amber"><i className="bx bx-network-chart"></i></div>
                                <div className="sc-stat-value">{stats.collaborationThreads}</div>
                                <div className="sc-stat-label">Collaboration threads</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon rose"><i className="bx bx-lock-alt"></i></div>
                                <div className="sc-stat-value">{stats.lockedThreads}</div>
                                <div className="sc-stat-label">Locked threads</div>
                            </div>
                        </div>

                        <div className="msg-workspace-grid">
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>Conversation rail</h3>
                                        <p>Find active conversations, filter by context, and jump straight into secure threads.</p>
                                    </div>
                                    <span className="sc-badge-status pending">{conversationPagination.total_items || conversations.length} threads</span>
                                </div>
                                <div className="sc-panel-body">
                                    <div className="sc-toolbar">
                                        <div className="sc-search">
                                            <i className="bx bx-search"></i>
                                            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations or participants" />
                                        </div>
                                        <select className="sc-select" value={contextFilter} onChange={(event) => setContextFilter(event.target.value as "all" | "general" | "collaboration" | "system")}>
                                            <option value="all">All</option>
                                            <option value="general">General</option>
                                            <option value="collaboration">Collaboration</option>
                                            <option value="system">System</option>
                                        </select>
                                    </div>

                                    {loadingWorkspace ? (
                                        <div className="sc-empty" style={{ padding: "46px 20px" }}>
                                            <i className="bx bx-loader-circle bx-spin"></i>
                                            <h3>Loading conversations</h3>
                                            <p>Preparing your messaging workspace.</p>
                                        </div>
                                    ) : conversations.length === 0 ? (
                                        <div className="sc-empty" style={{ padding: "46px 20px" }}>
                                            <i className="bx bx-message-square-dots"></i>
                                            <h3>No threads yet</h3>
                                            <p>Start a new conversation to begin secure messaging with your team.</p>
                                            <button type="button" className="sc-btn sc-btn-primary" onClick={() => setComposeOpen(true)}>Start a conversation</button>
                                        </div>
                                    ) : (
                                        <div className="msg-thread-list">
                                            {conversations.map((item) => (
                                                <button type="button" key={item.conversation_id} className={`msg-thread-item ${selectedConversationId === item.conversation_id ? "active" : ""}`} onClick={() => selectConversation(item)}>
                                                    <div className="msg-thread-item-top">
                                                        <div className="msg-thread-avatar">{item.subject.slice(0, 2).toUpperCase()}</div>
                                                        <div className="msg-thread-copy">
                                                            <h4>{item.subject}</h4>
                                                            <p>{item.last_message?.content || "No messages yet"}</p>
                                                        </div>
                                                        {item.unread_count > 0 ? <span className="msg-thread-unread">{item.unread_count}</span> : null}
                                                    </div>
                                                    <div className="msg-thread-meta">
                                                        <span><i className="bx bx-time-five"></i>{relativeTime(item.last_message?.created_at)}</span>
                                                        <span className={`msg-context-pill ${item.context_type}`}>{item.context_type}</span>
                                                        {item.is_locked ? <span className="msg-context-pill locked">locked</span> : null}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="msg-pagination-row">
                                        <small>Page {conversationPagination.current_page} of {conversationPagination.total_pages}</small>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!conversationPagination.has_previous} onClick={() => refreshWorkspace(conversationPagination.current_page - 1)}>
                                                <i className="bx bx-chevron-left"></i>
                                                Previous
                                            </button>
                                            <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!conversationPagination.has_next} onClick={() => refreshWorkspace(conversationPagination.current_page + 1)}>
                                                Next
                                                <i className="bx bx-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>{selectedConversation?.subject || "Conversation workspace"}</h3>
                                        <p>
                                            {selectedConversation
                                                ? `${selectedConversation.participants.map((item) => item.staff_name).join(", ")}${typingStates.length ? ` · ${typingStates.map((item) => item.staff_name).join(", ")} typing...` : ""}`
                                                : "Select a thread to see messages, status, and quick reply tools."}
                                        </p>
                                    </div>
                                    {selectedConversation ? (
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <span className={`msg-context-pill ${selectedConversation.context_type}`}>{selectedConversation.context_type}</span>
                                            {selectedConversation.is_locked ? <span className="msg-context-pill locked">protected</span> : null}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="sc-panel-body msg-thread-panel-body">
                                    {conversationBody()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />

            {composeOpen ? (
                <div className="sc-overlay" onClick={() => setComposeOpen(false)}>
                    <div className="sc-modal sc-modal-lg" onClick={(event) => event.stopPropagation()}>
                        <div className="sc-modal-header">
                            <h2>{collaborationId ? "Start collaboration conversation" : "Start a new conversation"}</h2>
                            <button type="button" className="sc-modal-close" onClick={() => setComposeOpen(false)}>
                                <i className="bx bx-x"></i>
                            </button>
                        </div>
                        <form onSubmit={createConversation}>
                            <div className="sc-modal-body">
                                <div className="sc-form-group">
                                    <label>Subject</label>
                                    <input className="sc-input" value={composeSubject} onChange={(event) => setComposeSubject(event.target.value)} placeholder="Give this conversation a clear subject" />
                                </div>
                                <div className="sc-form-group">
                                    <label>Participants</label>
                                    <select className="sc-input" multiple value={composeParticipants.map(String)} onChange={(event) => setComposeParticipants(Array.from(event.target.selectedOptions).map((option) => Number(option.value)))} style={{ minHeight: 190 }}>
                                        {otherStaff.map((person) => (
                                            <option key={person.id} value={person.id}>
                                                {person.full_name}{person.title_name ? ` · ${person.title_name}` : ""}{person.clinic_name ? ` · ${person.clinic_name}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sc-form-group">
                                    <label>Opening message</label>
                                    <textarea className="sc-textarea" rows={5} value={composeOpeningMessage} onChange={(event) => setComposeOpeningMessage(event.target.value)} placeholder={collaborationId ? "Add optional context for this collaboration thread" : "Add an optional opening note"} />
                                </div>
                            </div>
                            <div className="sc-modal-footer">
                                <button type="button" className="sc-btn sc-btn-ghost" onClick={() => setComposeOpen(false)}>Cancel</button>
                                <button type="submit" className="sc-btn sc-btn-primary" disabled={savingConversation}>
                                    <i className={`bx ${savingConversation ? "bx-loader-circle bx-spin" : "bx-send"}`}></i>
                                    {savingConversation ? "Creating..." : "Create conversation"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default MessagingWorkspacePage;
